// frontend/src/components/LabReportUpload/LabReportUpload.tsx
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import {
  InsertDriveFile,
  Delete,
  CheckCircle,
  Psychology,
  Preview,
  Refresh,
  PictureAsPdf,
  Image as ImageIcon,
} from '@mui/icons-material';
import VisionAIUploadDropzone from '../Common/VisionAIUploadDropzone';
import { formatFileSize, isPdfOrImageFile } from '../Common/visionUploadUtils';
import PdfPreview from '../Common/PdfPreview';
import LabReportTable from '../Common/LabReportTable';
import ImageGalleryDialog from './ImageGalleryDialog';
import { useLabReportUpload, UploadedFile } from '../../contexts/LabReportUploadContext';
import { useSession } from '../../contexts/SessionContext';
import { useUser } from '../../contexts/UserContext';

import { v4 as uuidv4 } from 'uuid'; // npm install uuid

interface DatabaseReport {
  report_id: string;
  visit_id?: string;
  patient_id?: string;
  type: string;
  date: string;
  status: string;
  data: any;
  confidence: number;
  file_type: string;
  original_filename?: string | null;
  created_at: string;
}

interface LabReportUploadProps {
  sessionId?: string;
  /** If set, saved-report list is filtered to this patient; otherwise derived from session / selected patient. */
  patientId?: number | string | null;
  mode?: string;
  onUploadComplete?: (file: UploadedFile) => void;
}

function labReportActualNameLabel(r: DatabaseReport) {
  const rawFromUpload = r.original_filename ? String(r.original_filename) : '';
  const rawBase = rawFromUpload
    ? rawFromUpload.replace(/\.[^/.]+$/, '')
    : (r.type || 'Lab').trim() || 'Lab';
  const safe = rawBase.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_') || 'Lab';

  const d = r.date
    ? String(r.date).slice(0, 10)
    : r.created_at
      ? new Date(r.created_at).toISOString().slice(0, 10)
      : 'unknown';

  const ext = (r.file_type || 'pdf').toLowerCase();
  return `${safe}_Report_${d}.${ext}`;
}


export default function LabReportUpload({
  sessionId,
  patientId: patientIdProp,
  onUploadComplete,
  mode = 'current_visit',
}: LabReportUploadProps) {
  // Use context for upload state (persists across tab switches)
  const { files, addFile, updateFile, removeFile } = useLabReportUpload();
  const { session, formSessionOverride } = useSession();
  const { patient } = useUser();

  const activeSession = formSessionOverride ?? session;
  const resolvedPatientId =
    patientIdProp ??
    activeSession?.patient_id ??
    patient?.patient_id ??
    null;

  const [allReports, setAllReports] = useState<DatabaseReport[]>([]);
  const [previewDialog, setPreviewDialog] = useState<{open: boolean, html: string, reportId: string}>({
    open: false,
    html: '',
    reportId: ''
  });
  const [imageGallery, setImageGallery] = useState<{
    open: boolean;
    reportId: string;
    images: Array<{
      page_number: number;
      image_data_base64: string;
      format: string;
      width: number;
      height: number;
    }>;
  }>({
    open: false,
    reportId: '',
    images: []
  });
  const [currentTab, setCurrentTab] = useState(0); // 0 = uploaded this session, 1 = saved reports for patient
  const [loading, setLoading] = useState(false);

  const fetchSavedLabReportsForPatient = useCallback(async () => {
    if (resolvedPatientId == null || String(resolvedPatientId).trim() === '') {
      setAllReports([]);
      return;
    }
    setLoading(true);
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const qs = new URLSearchParams({ limit: '100', patient_id: String(resolvedPatientId) });
      const response = await fetch(`${API_BASE_URL}/api/lab-reports/all?${qs.toString()}`, {
        headers: {
          'ngrok-skip-browser-warning': 'true',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAllReports(data.reports || []);
      }
    } catch (error) {
      console.error('Failed to fetch lab reports:', error);
    } finally {
      setLoading(false);
    }
  }, [resolvedPatientId]);

  useEffect(() => {
    void fetchSavedLabReportsForPatient();
  }, [fetchSavedLabReportsForPatient]);

  const handlePreview = async (reportId: string) => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      // const response = await fetch(`${API_BASE_URL}/api/lab-reports/${reportId}/preview`);
      const response = await fetch(`${API_BASE_URL}/api/lab-reports/${reportId}/preview`, {  
        headers: {
          'ngrok-skip-browser-warning': 'true', // Skip ngrok interstitial page
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewDialog({
          open: true,
          html: data.html,
          reportId: reportId
        });
      } else {
        alert('Failed to generate preview');
      }
    } catch (error) {
      console.error('Preview error:', error);
      alert('Failed to load preview');
    }
  };

  const closePreview = () => {
    setPreviewDialog({ open: false, html: '', reportId: '' });
  };

  const handlePreviewPdf = async (reportId: string) => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
    window.open(`${API_BASE_URL}/api/lab-reports/${reportId}/pdf`, '_blank');
  };

  const handlePreviewImages = async (reportId: string) => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      // const response = await fetch(`${API_BASE_URL}/api/lab-reports/${reportId}/images`);
      const response = await fetch(`${API_BASE_URL}/api/lab-reports/${reportId}/images`, {  
        headers: {
          'ngrok-skip-browser-warning': 'true', // Skip ngrok interstitial page
        },
      });


      if (response.ok) {
        const data = await response.json();
        // Show image gallery dialog
        setImageGallery({
          open: true,
          reportId: reportId,
          images: data.images
        });
      } else {
        alert('Failed to load images');
      }
    } catch (error) {
      console.error('Failed to load images:', error);
      alert('Failed to load images');
    }
  };

  const closeImageGallery = () => {
    setImageGallery({ open: false, reportId: '', images: [] });
  };

  const handleFiles = async (selectedFiles: File[]) => {
    const validFiles = selectedFiles.filter(isPdfOrImageFile);

    if (validFiles.length === 0) {
      alert('Please upload PDF or image files only');
      return;
    }

    const newFiles: UploadedFile[] = validFiles.map((file) => ({
      // id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      // id: crypto.randomUUID(),
      id: uuidv4(),
      name: file.name,
      size: file.size,
      type: file.type || 'application/octet-stream',
      uploadedAt: new Date(),
      status: 'uploading',
      progress: 0,
      mode,
    }));

    // Add files to context (persists across tab switches)
    newFiles.forEach((file) => addFile(file));

    // Upload to backend API
    for (let i = 0; i < validFiles.length; i++) {
      await uploadToBackend(validFiles[i], newFiles[i]);
    }
  };

  const uploadToBackend = async (actualFile: File, fileState: UploadedFile) => {
    try {
      // Create FormData
      const formData = new FormData();
      formData.append('file', actualFile);
      if (sessionId) {
        formData.append('session_id', sessionId);
      }
      formData.append('async_mode', 'true');

      // Upload to backend
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/lab-reports/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          'ngrok-skip-browser-warning': 'true', // Skip ngrok interstitial page
        },
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();

      console.log('Upload result:', result);
      console.log("LAB SUMMARY:", result.lab_summary);

      // if (result?.accepted && result?.job_id) {
      //   updateFile(fileState.id, { status: 'processing', progress: 15 });
      //   const job = await pollAsyncJob(String(result.job_id), {
      //     maxAttempts: 120,
      //     intervalMs: 1000,
      //     onProgress: (job: any) => {
      //     const pct = Number(job.progress_pct ?? 25);
      //     updateFile(fileState.id, { status: 'processing', progress: Math.min(95, Math.max(15, pct)) });
      //     },
      //   });
      //   if (job.status === 'failed') {
      //     throw new Error(job.error_message || 'Lab report job failed');
      //   }
      //   const ref = (job.result_ref || {}) as { report_id?: string; status?: string };
      //   updateFile(fileState.id, {
      //     status: 'completed',
      //     progress: 100,
      //     extractedData: { confidence: 1 },
      //     report_id: ref.report_id || undefined,
      //   });
      // } else {
      //   // Backward-compatible sync path
      //   updateFile(fileState.id, {
      //     status: 'completed',
      //     progress: 100,
      //     extractedData: result.extracted_data,
      //     report_id: result.file_id,
      //   });
      // }
      updateFile(fileState.id, {
  status: 'completed',
  progress: 100,
  extractedData: result.extracted_data,
  report_id: result.file_id,
  lab_summary: result.lab_summary,
  mode,
});

      fetchSavedLabReportsForPatient();

      // Call callback
      if (onUploadComplete) {
  onUploadComplete({
    ...fileState,
    status: 'completed',
    progress: 100,
    extractedData: result.extracted_data,
    report_id: result.file_id,
    lab_summary: result.lab_summary,
    mode,
  });
}
    } catch (error) {
      console.error('Upload error:', error);
      updateFile(fileState.id, { status: 'error', progress: 0 });
      alert(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDelete = (fileId: string) => {
    removeFile(fileId);
  };

  const handleView = (file: UploadedFile) => {
    console.log('View file:', file);
    // alert(`Viewing ${file.name}\n\nExtracted Data:\n${JSON.stringify(file.extractedData, null, 2)}`);
  };

  const getStatusColor = (status: UploadedFile['status']) => {
    switch (status) {
      case 'uploading':
        return '#1976d2';
      case 'processing':
        return '#ff9800';
      case 'completed':
        return '#009688';
      case 'error':
        return '#e91e63';
    }
  };

  return (
    <Box>
      {/* Tabs: in-session uploads vs saved reports for current patient */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label={`Current Session (${files.length})`} />
          <Tab
            label={`This patient (${allReports.length})`}
            icon={
              <Refresh
                onClick={(e) => {
                  e.stopPropagation();
                  fetchSavedLabReportsForPatient();
                }}
              />
            }
            iconPosition="end"
          />
        </Tabs>
      </Box>

      {/* Tab Panel 0: Current Session Uploads */}
      {currentTab === 0 && (
        <Box>
          <VisionAIUploadDropzone
            title="Upload Lab Reports"
            supportedFormatsText="PDF, JPG, PNG, GIF, BMP"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp"
            multiple
            onFilesSelected={handleFiles}
          />

      {/* Vision AI Info */}
      {files.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Vision AI Processing:</strong> Files are automatically processed using OCR and AI
            extraction to populate both structured fields and unstructured notes.
          </Typography>
        </Alert>
      )}

      {/* Uploaded Files List */}
      {files.length > 0 && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Uploaded Files ({files.length})
          </Typography>
          <List>
            {files.map((file) => (
              <ListItem
                key={file.id}
                sx={{
                  borderLeft: `4px solid ${getStatusColor(file.status)}`,
                  mb: 1,
                  bgcolor: '#fafafa',
                  borderRadius: 1,
                }}
              >
                <ListItemIcon>
                  {file.status === 'completed' ? (
                    <CheckCircle sx={{ color: '#009688' }} />
                  ) : file.status === 'processing' ? (
                    <Psychology sx={{ color: '#ff9800' }} />
                  ) : (
                    <InsertDriveFile sx={{ color: '#1976d2' }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">{file.name}</Typography>
                      <Chip
                        label={file.status}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(file.status),
                          color: 'white',
                          textTransform: 'capitalize',
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" display="block">
                        {formatFileSize(file.size)} • {file.uploadedAt.toLocaleTimeString()}
                      </Typography>
                      {(file.status === 'uploading' || file.status === 'processing') && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={file.progress}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: getStatusColor(file.status),
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                            {file.status === 'uploading' ? 'Uploading' : 'Processing with Vision AI'}...{' '}
                            {file.progress}%
                          </Typography>
                        </Box>
                      )}
                      {file.status === 'completed' && file.extractedData && (
                        <Chip
                          label={`Confidence: ${(file.extractedData.confidence * 100).toFixed(0)}%`}
                          size="small"
                          sx={{ mt: 1, bgcolor: '#009688', color: 'white' }}
                        />
                      )}
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {file.status === 'completed' && file.report_id && (
                    <>
                      <IconButton
                        onClick={() => handlePreviewPdf(file.report_id!)}
                        sx={{ color: '#1976d2' }}
                        title="View as PDF"
                      >
                        <PictureAsPdf />
                      </IconButton>
                      <IconButton
                        onClick={() => handlePreviewImages(file.report_id!)}
                        sx={{ color: '#1976d2' }}
                        title="View as Images"
                      >
                        <ImageIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handlePreview(file.report_id!)}
                        sx={{ color: '#1976d2' }}
                        title="Preview HTML"
                      >
                        <Preview />
                      </IconButton>
                    </>
                  )}
                  <IconButton onClick={() => handleDelete(file.id)} sx={{ color: '#e91e63' }}>
                    <Delete />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
        </Box>
      )}

      {/* Tab Panel 1: saved lab reports for this patient (API: /all?patient_id=) */}
      {currentTab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Lab reports for this patient ({allReports.length})
            </Typography>
            <Button
              startIcon={<Refresh />}
              onClick={fetchSavedLabReportsForPatient}
              disabled={loading || resolvedPatientId == null}
            >
              Refresh
            </Button>
          </Box>

          {resolvedPatientId == null ? (
            <Alert severity="info">
              Select a patient and start a visit to see lab reports saved for that patient.
            </Alert>
          ) : loading ? (
            <LinearProgress />
          ) : allReports.length === 0 ? (
            <Alert severity="info">No lab reports found for this patient yet.</Alert>
          ) : (
            <List
              sx={{
                maxHeight: '600px',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#bdbdbd',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#9e9e9e',
                  },
                },
              }}
            >
              {allReports.map((report) => (
                <ListItem
                  key={report.report_id}
                  sx={{
                    borderLeft: '4px solid #009688',
                    mb: 1,
                    bgcolor: '#fafafa',
                    borderRadius: 1,
                  }}
                >
                  <ListItemIcon>
                    <InsertDriveFile sx={{ color: '#1976d2' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">{labReportActualNameLabel(report)}</Typography>
                        <Chip
                          label={report.file_type.toUpperCase()}
                          size="small"
                          sx={{ bgcolor: '#1976d2', color: 'white' }}
                        />
                        <Chip
                          label={`${(report.confidence * 100).toFixed(0)}% confidence`}
                          size="small"
                          sx={{ bgcolor: '#009688', color: 'white' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" display="block">
                          Date: {report.date} • Created: {new Date(report.created_at).toLocaleString()}
                        </Typography>
                        {report.visit_id && (
                          <Typography variant="caption" display="block">
                            Visit ID: {report.visit_id}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      onClick={() => handlePreviewPdf(report.report_id)}
                      sx={{ color: '#1976d2' }}
                      title="View as PDF"
                    >
                      <PictureAsPdf />
                    </IconButton>
                    <IconButton
                      onClick={() => handlePreviewImages(report.report_id)}
                      sx={{ color: '#1976d2' }}
                      title="View as Images"
                    >
                      <ImageIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handlePreview(report.report_id)}
                      sx={{ color: '#1976d2' }}
                      title="Preview HTML"
                    >
                      <Preview />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* HTML Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={closePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Lab Report Preview</DialogTitle>
        <DialogContent>
          <Box
            sx={{ mt: 1 }}
            dangerouslySetInnerHTML={{ __html: previewDialog.html }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Image Gallery Dialog */}
      <ImageGalleryDialog
        open={imageGallery.open}
        reportId={imageGallery.reportId}
        images={imageGallery.images}
        onClose={closeImageGallery}
      />
    </Box>
  );
}
